import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { PACK_SETS } from '@listen-draw/shared';
import { t, useLocale } from '../lib/i18n.js';

export function Help() {
  useLocale();
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (status === 'sending') return;
    setStatus('sending');
    try {
      const response = await fetch('/api/feedback', {
        signal: AbortSignal.timeout(12000),
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ message: message.trim() }),
      });
      if (!response.ok) throw new Error();
      setStatus('sent');
      setMessage('');
    } catch {
      setStatus('error');
    }
  };
  return (
    <main className="page reading-page">
      <p className="eyebrow">{t('A quick field guide', 'かんたんガイド')}</p>
      <h1>{t('A good game starts with good listening.', '聞ければ、もう遊べる。')}</h1>
      <section>
        <h2>{t('Your first round', '最初のラウンド')}</h2>
        <ol>
          <li>
            {t(
              'Turn up your volume. Use Test my sound before you begin.',
              '音量を上げ、開始前に「音声をテスト」で確認します。',
            )}
          </li>
          <li>
            {t(
              'Hear four short sentences, labeled A, B, C and D. The text stays hidden.',
              'A〜D の短い英文を聞きます。文字は伏せられています。',
            )}
          </li>
          <li>
            {t(
              'Watch the sketch appear, then choose the matching sentence. You do not draw or speak.',
              '少しずつ現れる絵を見て、合う英文を選びます。絵を描いたり話したりする必要はありません。',
            )}
          </li>
          <li>
            {t(
              'Choose once. Review all four sentences and the key words at the reveal.',
              '回答は一度だけ。答え合わせで4つの英文と決め手の語を確認します。',
            )}
          </li>
        </ol>
        <Link className="btn btn--primary" to="/solo">
          {t('Try a round →', '一人で遊ぶ →')}
        </Link>
      </section>
      <section>
        <h2>{t('Learn from the correct sentence', '正解の英文から学ぶ')}</h2>
        <p>
          {t(
            'After the reveal, open Sentence breakdown for Japanese study notes: a translation, subject–verb structure, modifier links and the two listening clues. Available for every bundled sentence in solo, daily and hosted games.',
            '答え合わせ後の「正解の英文を解説」を開くと、日本語訳、主語・動詞などの構造、修飾語のつながり、聞き分けの決め手を確認できます。同梱問題の全英文に対応し、一人用・今日の一問・グループ対戦で利用できます。',
          )}
        </p>
      </section>
      <section>
        <h2>{t('Choose your speech speed', '読み上げ速度を選ぶ')}</h2>
        <p>
          {t(
            'Choose Slow (0.8×), Standard (1×), or Faster (1.2×). Solo remembers your choice on this device and uses it for the sound test, clues and answer replay. In a group game, the host chooses the speed for everyone.',
            'ゆっくり（0.8倍）・標準（1倍）・速め（1.2倍）から選べます。一人用は端末に設定を保存し、音声テスト・出題・正解の聞き直しに反映します。グループ対戦はホストが全員共通の速度を選びます。',
          )}
        </p>
      </section>
      <section>
        <h2>{t('How scoring works', '得点のルール')}</h2>
        <div className="scorebox">
          <div>
            <b>+300</b>
            <span>{t('0–33% complete', '完成率0〜33%')}</span>
          </div>
          <div>
            <b>+200</b>
            <span>{t('34–66% complete', '完成率34〜66%')}</span>
          </div>
          <div>
            <b>+100</b>
            <span>{t('67–100% complete', '完成率67〜100%')}</span>
          </div>
          <div>
            <b>−100</b>
            <span>{t('Wrong answer', '誤答')}</span>
          </div>
        </div>
        <p>
          {t(
            'In solo rounds, you have 18 seconds of drawing time. No answer earns 0 points. In hosted games, an optional final window awards 50 points; the host can change the wrong-answer penalty and allow hints. Replaying the clues in a hosted game halves positive points for everyone.',
            '一人用では描画時間は18秒。未回答は0点です。ホスト式では完成後の最終回答で50点を獲得でき、誤答の減点やヒントを設定できます。ホスト式の聞き直しでは全員の正解時の得点が半分になります。',
          )}
        </p>
      </section>
      <section>
        <h2>{t('Host a game', 'ホストとして遊ぶ')}</h2>
        <ol>
          <li>
            {t(
              'Create a room on a computer or tablet. Rooms start unlisted; share the join link or QR code with your group.',
              'PCやタブレットで部屋を作ります。最初は一覧に非公開です。参加リンクかQRをグループに共有します。',
            )}
          </li>
          <li>
            {t(
              'For a classroom, play audio on the host and use speakers. For online play, choose Players so everyone hears the clues on their own device.',
              '教室ではホスト端末からスピーカーで再生。オンラインでは「参加者のみ」を選ぶと各端末で音声が流れます。',
            )}
          </li>
          <li>
            {t(
              'Everyone who uses device audio should tap Enable sound before the game. Keep the host tab open during the game.',
              '各端末で音声を聞く場合は、開始前に「音声を有効にする」を押してください。ホストのタブは開いたままにします。',
            )}
          </li>
          <li>
            {t(
              'Start with a small group and test one round. Players get one answer each. The earliest correct player wins the round.',
              '少人数で一問テストしてから始めます。回答は一人一回。最も早く正解した参加者がラウンドの勝者です。',
            )}
          </li>
          <li>
            {t(
              'Use the reveal to discuss the key words, then move to the next round. You can remove a player from the lobby if needed.',
              '答え合わせで決め手の語を確認し、次のラウンドへ進みます。必要に応じてロビーで参加者を退出させられます。',
            )}
          </li>
        </ol>
        <p>
          {t(
            'The room limit is 60 players. This is a software limit, not a guarantee for every network. Room codes expire after 90 minutes of inactivity. Save your result before leaving; there is no account-based history.',
            '上限は1部屋60人です。ソフトウェア上の上限であり、どの回線でも同じ品質を保証するものではありません。90分間操作のない部屋は終了します。アカウントに紐づく履歴はないため、結果は退出前に保存してください。',
          )}
        </p>
        <Link className="btn" to="/#host">
          {t('Create a room →', '部屋を作る →')}
        </Link>
      </section>
      <section>
        <h2>{t('Sound or connection trouble?', '音声・接続で困ったら')}</h2>
        <ul>
          <li>
            {t(
              'Tap the sound button and check volume, output device and headphones. Try opening the game directly in Safari, Chrome, Edge or Firefox instead of a social app’s browser.',
              '音声ボタン、音量、出力先、イヤホンを確認してください。SNSアプリ内ブラウザの場合はSafari、Chrome、Edge、Firefoxで直接開いてみてください。',
            )}
          </li>
          <li>
            {t(
              'If downloading audio fails, reconnect and restart. Curated rounds use bundled recordings, so you do not need to install an English system voice.',
              '音声を読み込めなければ、再接続してやり直してください。同梱問題の音声は録音ファイルなので、OSへの英語音声追加は不要です。',
            )}
          </li>
          <li>
            {t(
              'A solo round pauses when you leave the tab. Restarting it is marked as practice. Shared links preserve the question and timing, but they are casual challenges, not a verified global leaderboard.',
              '一人用ではタブを離れると停止し、再開は練習になります。共有リンクの問題と条件は同じですが、記録は気軽な対戦用で、検証済みの世界ランキングではありません。',
            )}
          </li>
          <li>
            {t(
              'If a hosted game disconnects, reopen the same link in the same browser. If the room has closed, ask the host for a new code or play solo.',
              'ホスト式で切断したら、同じブラウザで同じリンクを開き直してください。部屋が終了していたら新しいコードを確認するか、一人用で遊べます。',
            )}
          </li>
        </ul>
      </section>
      <section>
        <h2>{t('About this version', 'このバージョンについて')}</h2>
        <p>
          {t(
            `The public game is free, with ${PACK_SETS.length} original curated question sets and pre-generated English audio. It does not play back exam recordings. The code also supports experimental AI-generated rounds, but the public curated experience does not depend on a live AI service.`,
            `公開版は無料で、${PACK_SETS.length}セットのオリジナル問題と事前生成した英語音声を収録しています。試験の音源は再生しません。コードには実験的なAI生成機能もありますが、公開版の同梱問題は外部AIの稼働に依存しません。`,
          )}
        </p>
        <p>
          {t(
            'You are welcome to show gameplay in classes, streams and videos, including monetized videos. Please credit Listen & Draw and link to the game. Ask participants before recording or showing their names.',
            '授業、配信、動画でゲーム画面を紹介できます。動画の収益化も可能です。Listen & Drawの名前とゲームへのリンクを添えてください。参加者の撮影や名前の表示は本人に確認してください。',
          )}
        </p>
      </section>
      <section id="feedback">
        <h2>
          {t('Tell me where it clicked. Or where it didn’t.', 'ひらめいた瞬間も、困ったことも。')}
        </h2>
        <p>
          {t(
            'Made by phunter_jp. Send a question or bug report on X, or leave a short note below.',
            '制作者は phunter_jp です。質問や不具合はXへ、または下のフォームからお寄せください。',
          )}{' '}
          <a href="https://x.com/phunter_jp" target="_blank" rel="noreferrer">
            @phunter_jp ↗
          </a>
        </p>
        <form className="feedback-form" onSubmit={(event) => void submit(event)}>
          <label htmlFor="feedback-message">{t('Your feedback', 'ご意見')}</label>
          <textarea
            id="feedback-message"
            minLength={5}
            maxLength={1000}
            required
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            placeholder={t(
              'What worked? What could be clearer?',
              '遊んだ感想や、わかりにくかったことを教えてください。',
            )}
          />
          <p className="muted small">
            {t(
              'This form is for feedback, without a reply address. For a reply, contact @phunter_jp on X. Please avoid personal or sensitive information.',
              'このフォームでは返信先を受け取りません。返信が必要な場合はXの @phunter_jp にご連絡ください。個人情報や機密情報は入力しないでください。',
            )}
          </p>
          <button
            className="btn btn--primary"
            disabled={message.trim().length < 5 || status === 'sending'}
          >
            {status === 'sending' ? t('Sending…', '送信中…') : t('Send feedback', '送信する')}
          </button>
          <p role="status">
            {status === 'sent'
              ? t(
                  'Thank you. Your feedback was saved.',
                  'ありがとうございます。ご意見を保存しました。',
                )
              : status === 'error'
                ? t(
                    'Could not send. Wait a minute and try again, or contact us on X.',
                    '送信できませんでした。1分待って再度お試しいただくか、Xへご連絡ください。',
                  )
                : ''}
          </p>
        </form>
      </section>
    </main>
  );
}
